/* FVの出現演出。背景以外の要素を1本のrAFでまとめて回す。
   canvas型は起動時に一度だけラスタライズし、以降は帯へ刻んでずらすだけにする。
   テキストは実テキストのまま明滅だけを当て、選択とSEOを維持する。 */

import { TITLE_IDLE_GLITCH_CONFIG } from "./_titleGlitch.es6";

const GLITCH = {
    amp: 0.04, // 帯のずれ幅。要素の横幅に対する比率
    bandH: 9, // 帯の高さ(px)。本数は要素の高さから決まる
    black: 0.1, // 全体の明滅の頻度
    decay: 2.2, // 収まり方
    drop: 0.15, // 帯の欠落率
    duration: 850, // 1要素あたりの長さ(ms)
    jitter: 0.018, // 全体の横揺れ。要素の横幅に対する比率
    offset: 500, // 演出全体の開始を遅らせる時間(ms)
};

// rgbは3つ足すと白へ戻る色で分解するため、着地点の色は変わらない。
// 4TH SEASON(#96FF00)は青成分が0なので分解しない。
const ELEMENTS = [
    { delay: 500, key: "logo", rgb: 0.6, type: "image" },
    { delay: 500, key: "howto", rgb: 1, type: "howto" },
    { delay: 500, key: "kv", rgb: 1, type: "image" },
    { delay: 500, idle: true, key: "season", rgb: 0, type: "image" },
    { delay: 500, key: "lead", type: "text" },
    { delay: 500, key: "body", type: "text" },
];

const HOWTO_WIDTH = 404;
const HOWTO_HEIGHT = 115;
const HOWTO_FILL = "#e6e6e6";
const HOWTO_NOISE = {
    boost: 0.3,
    density: 0.51,
    frames: 8,
    opacity: 0.25,
};
const HOWTO_PATH =
    "M73.1264 2.35385V3.92308C71.4002 5.02154 69.5171 6.27693 67.9479 8.7877V105.766C69.5171 108.277 71.4002 109.532 73.1264 110.631V112.2H37.6617V110.631C39.3879 109.532 41.271 108.277 42.8402 105.766V55.7077H30.2864V105.766C31.8556 108.277 33.7387 109.532 35.4648 110.631V112.2H0.000201943V110.631C1.72636 109.532 3.60943 108.277 5.17866 105.766V8.7877C3.60943 6.27693 1.72636 5.02154 0.000201943 3.92308V2.35385H35.4648V3.92308C33.7387 5.02154 31.8556 6.27693 30.2864 8.7877V51H42.8402V8.7877C41.271 6.27693 39.3879 5.02154 37.6617 3.92308V2.35385H73.1264ZM113.12 114.554C94.4458 114.554 77.812 91.6431 77.812 57.2769C77.812 22.9108 94.4458 4.45146e-06 113.12 4.45146e-06C131.794 4.45146e-06 148.427 22.9108 148.427 57.2769C148.427 91.6431 131.794 114.554 113.12 114.554ZM113.12 109.846C120.966 109.846 122.535 93.84 122.535 57.2769C122.535 20.7139 120.966 4.70769 113.12 4.70769C105.274 4.70769 103.704 20.7139 103.704 57.2769C103.704 93.84 105.274 109.846 113.12 109.846ZM174.12 113.769H172.551L150.268 8.63077C148.541 6.43385 146.188 4.86462 144.461 3.92308V2.35385H179.141V3.92308C177.572 4.86462 176.788 5.64924 175.846 8.63077L184.32 55.8646L198.757 1.56923H200.326L215.077 57.4339L223.551 10.9846C221.197 7.06155 217.901 5.02154 213.664 3.92308V2.35385H230.141L209.271 113.769H207.701L190.44 51.1569L174.12 113.769ZM312.254 112.2H276.789V110.631C278.515 109.532 280.398 108.277 281.968 105.766V7.06155H280.398C276.946 12.7108 274.906 16.4769 261.725 41.4277H260.155L262.509 2.35385H326.534L328.888 41.4277H327.318C314.137 16.4769 312.097 12.7108 308.645 7.06155H307.075V105.766C308.645 108.277 310.528 109.532 312.254 110.631V112.2ZM368.12 114.554C349.446 114.554 332.812 91.6431 332.812 57.2769C332.812 22.9108 349.446 4.45146e-06 368.12 4.45146e-06C386.794 4.45146e-06 403.427 22.9108 403.427 57.2769C403.427 91.6431 386.794 114.554 368.12 114.554ZM368.12 109.846C375.966 109.846 377.535 93.84 377.535 57.2769C377.535 20.7139 375.966 4.70769 368.12 4.70769C360.274 4.70769 358.704 20.7139 358.704 57.2769C358.704 93.84 360.274 109.846 368.12 109.846Z";

const createCanvas = (width, height) =>
    Object.assign(document.createElement("canvas"), {
        height,
        width,
    });

// 3チャンネルの係数を足すと白になるため、ずれがない状態では元の色へ戻る。
const tint = (index, chroma) => {
    const high = Math.round((1 / 3 + (2 * chroma) / 3) * 255);
    const low = Math.round((1 / 3 - chroma / 3) * 255);
    const color = [low, low, low];

    color[index] = high;

    return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
};

const colorChannel = (source, target, color) => {
    const context = target.getContext("2d");

    context.clearRect(0, 0, target.width, target.height);
    context.drawImage(source, 0, 0);
    context.globalCompositeOperation = "multiply";
    context.fillStyle = color;
    context.fillRect(0, 0, target.width, target.height);
    context.globalCompositeOperation = "destination-in";
    context.drawImage(source, 0, 0);
    context.globalCompositeOperation = "source-over";
};

class GlitchItem {
    constructor($element, option) {
        this.$element = $element;
        this.delay = option.delay;
        this.isSettled = false;
    }

    settle() {
        if (this.isSettled) return;

        this.isSettled = true;
        this.$element.classList.add("-glitch-complete");
    }
}

class GlitchTextItem extends GlitchItem {
    prepare() {
        return Promise.resolve(true);
    }

    draw(progress) {
        const attenuation = (1 - progress) ** GLITCH.decay;
        // 横幅に対する比率で振るため、canvas側と同じ揺れ幅になる。
        const jitter = (Math.random() * 2 - 1) * attenuation * GLITCH.jitter * 100;

        this.$element.style.opacity = Math.random() < GLITCH.black * attenuation + 0.015 ? "0" : "1";
        this.$element.style.transform = `translate3d(${jitter}%, 0, 0)`;
    }

    settle() {
        this.$element.style.opacity = "";
        this.$element.style.transform = "";
        super.settle();
    }
}

class GlitchCanvasItem extends GlitchItem {
    constructor($element, option) {
        super($element, option);

        this.hasIdle = option.idle || false;
        this.rgb = option.rgb;
        this.$canvas = $element.querySelector(".howto4th-fv__glitch-canvas");
        // 描画元が毎フレーム変わるかどうか。変わらないならチャンネルを使い回す。
        this.isDynamic = false;
        this.hasChannels = false;
    }

    prepare() {
        const rect = this.$element.getBoundingClientRect();
        const dpr = Math.min(2, window.devicePixelRatio || 1);

        this.dpr = dpr;
        this.width = Math.round(rect.width * dpr);
        this.height = Math.round(rect.height * dpr);

        if (!this.$canvas || !this.width || !this.height) return Promise.resolve(false);

        this.context = this.$canvas.getContext("2d");

        if (!this.context) return Promise.resolve(false);

        this.$canvas.width = this.width;
        this.$canvas.height = this.height;
        this.context.imageSmoothingEnabled = false;
        // 帯の高さは表示px基準で揃え、画面密度で太さが変わらないようにする。
        this.bands = Math.max(1, Math.round(this.height / (GLITCH.bandH * dpr)));
        this.channels = this.rgb ? [createCanvas(this.width, this.height), createCanvas(this.width, this.height), createCanvas(this.width, this.height)] : [];

        return this.load();
    }

    source() {
        return this.base;
    }

    updateChannels(source) {
        if (this.hasChannels && !this.isDynamic) return;

        this.hasChannels = true;
        this.channels.forEach((channel, index) => colorChannel(source, channel, tint(index, this.rgb)));
    }

    slice(source, amplitude, drop, offset, bands = this.bands) {
        const bandHeight = this.height / bands;

        for (let band = 0; band < bands; band += 1) {
            if (Math.random() < drop) continue;

            const y = band * bandHeight;
            const x = offset + (Math.random() * 2 - 1) * amplitude;

            this.context.drawImage(source, 0, y, this.width, bandHeight, x, y, this.width, bandHeight);
        }
    }

    draw(progress) {
        const attenuation = (1 - progress) ** GLITCH.decay;

        this.context.clearRect(0, 0, this.width, this.height);

        if (progress < 0.92 && Math.random() < GLITCH.black * attenuation + 0.015) return;

        const source = this.source(attenuation);
        const amplitude = attenuation * GLITCH.amp * this.width;
        const drop = attenuation * GLITCH.drop;
        const jitter = (Math.random() * 2 - 1) * attenuation * GLITCH.jitter * this.width;

        if (!this.rgb) {
            this.slice(source, amplitude, drop, jitter);
            return;
        }

        const offset = attenuation * GLITCH.amp * this.width * this.rgb;

        this.updateChannels(source);
        this.context.globalCompositeOperation = "lighter";
        this.slice(this.channels[0], amplitude, drop, jitter - offset);
        this.slice(this.channels[1], amplitude, drop, jitter);
        this.slice(this.channels[2], amplitude, drop, jitter + offset);
        this.context.globalCompositeOperation = "source-over";
    }

    settle() {
        if (this.isSettled) return;

        this.context.clearRect(0, 0, this.width, this.height);
        this.context.drawImage(this.source(0), 0, 0);
        super.settle();

        if (this.hasIdle) this.startIdle();
    }

    startIdle() {
        this.idleInView = false;
        this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        this.handleIdleAvailability = this.handleIdleAvailability.bind(this);
        document.addEventListener("visibilitychange", this.handleIdleAvailability);

        if (this.reducedMotionQuery.addEventListener) {
            this.reducedMotionQuery.addEventListener("change", this.handleIdleAvailability);
        } else {
            this.reducedMotionQuery.addListener(this.handleIdleAvailability);
        }

        if (!("IntersectionObserver" in window)) {
            this.idleInView = true;
            this.scheduleIdle();
            return;
        }

        this.idleObserver = new IntersectionObserver(
            (entries) => {
                this.idleInView = entries[0].isIntersecting;
                this.handleIdleAvailability();
            },
            { threshold: TITLE_IDLE_GLITCH_CONFIG.threshold }
        );
        this.idleObserver.observe(this.$element);
    }

    handleIdleAvailability() {
        if (this.canRunIdle()) {
            this.scheduleIdle();
            return;
        }

        this.stopIdle();
    }

    canRunIdle() {
        return this.idleInView && !document.hidden && !this.reducedMotionQuery.matches;
    }

    scheduleIdle() {
        window.clearTimeout(this.idleTimer);

        if (!this.canRunIdle() || this.idleRaf) return;

        const delay = TITLE_IDLE_GLITCH_CONFIG.minDelay + Math.random() * (TITLE_IDLE_GLITCH_CONFIG.maxDelay - TITLE_IDLE_GLITCH_CONFIG.minDelay);

        this.idleTimer = window.setTimeout(() => this.runIdleBurst(), delay);
    }

    runIdleBurst() {
        this.idleTimer = null;

        if (!this.canRunIdle()) return;

        const duration = TITLE_IDLE_GLITCH_CONFIG.minDuration + Math.random() * (TITLE_IDLE_GLITCH_CONFIG.maxDuration - TITLE_IDLE_GLITCH_CONFIG.minDuration);
        let startTime = 0;

        this.$element.classList.add("-idle-glitch");

        const tick = (now) => {
            if (!this.canRunIdle()) {
                this.stopIdle();
                return;
            }

            if (!startTime) startTime = now;

            const progress = (now - startTime) / duration;

            if (progress >= 1) {
                this.settleIdle();
                this.idleRaf = null;
                this.scheduleIdle();
                return;
            }

            this.drawIdle(progress);
            this.idleRaf = window.requestAnimationFrame(tick);
        };

        this.idleRaf = window.requestAnimationFrame(tick);
    }

    drawIdle(progress) {
        const config = TITLE_IDLE_GLITCH_CONFIG;
        const attenuation = (1 - progress) ** config.decay;

        this.context.clearRect(0, 0, this.width, this.height);

        if (progress < 0.9 && Math.random() < config.black * attenuation + 0.015) return;

        const source = this.source(attenuation);
        const amplitude = attenuation * config.amplitude * this.dpr;
        const drop = attenuation * config.drop;
        const jitter = (Math.random() * 2 - 1) * attenuation * config.jitter * this.dpr;

        if (!this.rgb) {
            this.slice(source, amplitude, drop, jitter, config.bands);
            return;
        }

        const offset = attenuation * config.amplitude * this.dpr * this.rgb;

        this.updateChannels(source);
        this.context.globalCompositeOperation = "lighter";
        this.slice(this.channels[0], amplitude, drop, jitter - offset, config.bands);
        this.slice(this.channels[1], amplitude, drop, jitter, config.bands);
        this.slice(this.channels[2], amplitude, drop, jitter + offset, config.bands);
        this.context.globalCompositeOperation = "source-over";
    }

    settleIdle() {
        this.context.clearRect(0, 0, this.width, this.height);
        this.context.drawImage(this.source(0), 0, 0);
        this.$element.classList.remove("-idle-glitch");
    }

    stopIdle() {
        window.clearTimeout(this.idleTimer);
        this.idleTimer = null;

        if (this.idleRaf) window.cancelAnimationFrame(this.idleRaf);

        this.idleRaf = null;
        this.settleIdle();
    }
}

class GlitchImageItem extends GlitchCanvasItem {
    // 表示側がobject-fit: coverのため、ラスタライズも同じ切り取り方へ合わせる。
    drawCover(context, image) {
        if (!image.naturalWidth || !image.naturalHeight) {
            context.drawImage(image, 0, 0, this.width, this.height);
            return;
        }

        const scale = Math.max(this.width / image.naturalWidth, this.height / image.naturalHeight);
        const width = image.naturalWidth * scale;
        const height = image.naturalHeight * scale;

        context.drawImage(image, (this.width - width) / 2, (this.height - height) / 2, width, height);
    }

    load() {
        return new Promise((resolve) => {
            const $image = this.$element.querySelector("img");

            if (!$image) {
                resolve(false);
                return;
            }

            const image = new Image();

            image.onload = () => {
                this.base = createCanvas(this.width, this.height);
                this.drawCover(this.base.getContext("2d"), image);
                resolve(true);
            };
            image.onerror = () => resolve(false);
            // pictureで出し分けている場合があるため、実際に表示している画像を使う。
            image.src = $image.currentSrc || $image.src;
        });
    }
}

class GlitchHowToItem extends GlitchCanvasItem {
    load() {
        this.isDynamic = true;
        this.path = new Path2D(HOWTO_PATH);
        this.temporary = createCanvas(this.width, this.height);
        this.noise = [];
        this.frame = 0;

        const random = () => (Math.random() + Math.random() + Math.random()) / 3;
        const saturate = (value) => Math.min(1, Math.max(0, (value - 0.5) * 2 + 0.5));

        // ノイズはデザイン寸法で作り、描画時に拡大する。画面密度ごとに作り直さない。
        for (let frame = 0; frame < HOWTO_NOISE.frames; frame += 1) {
            const $noise = createCanvas(HOWTO_WIDTH, HOWTO_HEIGHT);
            const context = $noise.getContext("2d");
            const imageData = context.createImageData(HOWTO_WIDTH, HOWTO_HEIGHT);

            for (let index = 0; index < HOWTO_WIDTH * HOWTO_HEIGHT; index += 1) {
                if (Math.random() >= HOWTO_NOISE.density) continue;

                const offset = index * 4;

                imageData.data[offset] = saturate(random()) * 255;
                imageData.data[offset + 1] = saturate(random()) * 255;
                imageData.data[offset + 2] = saturate(random()) * 255;
                imageData.data[offset + 3] = 255;
            }

            context.putImageData(imageData, 0, 0);
            this.noise.push($noise);
        }

        return Promise.resolve(true);
    }

    source(attenuation) {
        const context = this.temporary.getContext("2d");

        this.frame += 1;
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, this.width, this.height);
        context.fillStyle = HOWTO_FILL;
        context.fillRect(0, 0, this.width, this.height);
        context.globalAlpha = Math.min(1, HOWTO_NOISE.opacity + attenuation * HOWTO_NOISE.boost);
        context.imageSmoothingEnabled = false;
        context.drawImage(this.noise[this.frame % this.noise.length], 0, 0, this.width, this.height);
        context.globalAlpha = 1;
        context.globalCompositeOperation = "destination-in";
        context.setTransform(this.width / HOWTO_WIDTH, 0, 0, this.height / HOWTO_HEIGHT, 0, 0);
        context.fill(this.path);
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.globalCompositeOperation = "source-over";

        return this.temporary;
    }
}

const createItem = (option) => {
    const $element = document.querySelector(`[data-fv-glitch="${option.key}"]`);

    if (!$element) return null;
    if (option.type === "text") return new GlitchTextItem($element, option);
    if (option.type === "howto") return new GlitchHowToItem($element, option);

    return new GlitchImageItem($element, option);
};

export default class FvGlitch {
    constructor() {
        this.items = ELEMENTS.map(createItem).filter((item) => item);
    }

    async start() {
        if (!this.items.length) return;

        const results = await Promise.all(this.items.map((item) => item.prepare()));
        const items = this.items.filter((item, index) => {
            // 準備できなかった要素は演出を飛ばし、着地状態だけ出す。
            if (!results[index]) item.settle();

            return results[index];
        });

        if (!items.length) return;

        window.requestAnimationFrame((startTime) => this.run(items, startTime));
    }

    run(items, startTime) {
        const tick = (now) => {
            const elapsed = now - startTime;
            let isRunning = false;

            items.forEach((item) => {
                const progress = (elapsed - GLITCH.offset - item.delay) / GLITCH.duration;

                if (progress < 0) {
                    isRunning = true;
                    return;
                }

                if (progress >= 1) {
                    item.settle();
                    return;
                }

                isRunning = true;
                item.draw(progress);
            });

            if (isRunning) window.requestAnimationFrame(tick);
        };

        window.requestAnimationFrame(tick);
    }
}
